/**
 * Security Settings Hooks
 * 
 * TanStack Query hooks for security settings operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { securityService } from '../services/security-service';
import { getCurrentUserId } from '../lib/supabase';
import type {
  UpdateSecuritySettingsInput,
} from '../types/security.types';

/**
 * Query keys for security settings queries
 */
export const securityKeys = {
  all: ['security'] as const,
  current: () => [...securityKeys.all, 'current'] as const,
};

/**
 * Hook to get current user's security settings
 */
export function useSecuritySettings() {
  return useQuery({
    queryKey: securityKeys.current(),
    queryFn: async () => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      const result = await securityService.getSecuritySettings(userId);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to update security settings
 */
export function useUpdateSecuritySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSecuritySettingsInput) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      const result = await securityService.updateSecuritySettings(userId, input);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    onSuccess: async () => {
      // Invalidate security settings query to refetch updated data
      await queryClient.invalidateQueries({ queryKey: securityKeys.current() });
    },
  });
}

